import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error("Failed to fetch user:", error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, {
      email,
      password,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, {
      name,
      email,
      password,
    });
    return response.data;
  };

  const registerViaInvite = async (inviteCode, name, email, password) => {
    const response = await axios.post(`${API_URL}/api/invite/register`, {
      invite_code: inviteCode,
      name,
      email,
      password,
    });
    return response.data;
  };

  const resendConfirmation = async (email) => {
    const response = await axios.post(`${API_URL}/api/auth/resend-confirmation`, { email });
    return response.data;
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
    return response.data;
  };

  const resetPassword = async (tokenValue, newPassword) => {
    const response = await axios.post(`${API_URL}/api/auth/reset-password`, {
      token: tokenValue,
      new_password: newPassword,
    });
    return response.data;
  };

  const joinViaInvite = async (inviteCode) => {
    const response = await axios.post(`${API_URL}/api/invite/join`, {
      invite_code: inviteCode,
    });
    setUser(response.data);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  const updateCurrentUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    registerViaInvite,
    resendConfirmation,
    forgotPassword,
    resetPassword,
    joinViaInvite,
    updateCurrentUser,
    refreshUser: fetchUser,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
